import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LayoutTemplate, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { TEMPLATES } from "@/storefront/templates";

export default function AdminTemplates() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-templates"],
    queryFn: async () => (await api.get("/admin/templates")).data,
  });
  const update = useMutation({
    mutationFn: async ({ id, body }) => api.put(`/admin/templates/${id}`, body),
    onSuccess: () => { toast.success("Template settings saved"); qc.invalidateQueries({ queryKey: ["admin-templates"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const rows = (data?.templates || []).map((t) => ({ ...t, meta: TEMPLATES[t.id] }));

  return (
    <div data-testid="admin-templates-page">
      <PageHeader title="Storefront Templates" sub="Control which of the 7 premium templates agents can use, set the default, and see live usage." testid="admin-templates-title" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-template-grid">
        {rows.map((t) => (
          <div key={t.id} data-testid={`admin-template-${t.id}`}
            className={`overflow-hidden rounded-xl border-2 bg-white shadow-sm ${t.is_default ? "border-[#FF6B00]" : "border-slate-100"}`}>
            <div className="flex h-24">
              {t.meta && [t.meta.tokens.bg, t.meta.tokens.primary, t.meta.tokens.accent].map((c) => (
                <div key={c} className="flex-1" style={{ background: c }} />
              ))}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] text-slate-400">TEMPLATE {t.num}</p>
                {t.is_default && <span className="flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[9px] font-bold text-[#E85D04]"><Star size={9} /> DEFAULT</span>}
              </div>
              <p className="text-sm font-bold text-[#0F2440]">{t.name}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">{t.meta?.tagline}</p>
              <p className="mt-2 text-xs font-semibold text-slate-600" data-testid={`usage-${t.id}`}>
                {t.usage} store{t.usage === 1 ? "" : "s"} using this
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" data-testid={`toggle-template-${t.id}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ id: t.id, body: { enabled: !t.enabled } })}
                  className={t.enabled ? "border-emerald-200 text-emerald-600" : "border-rose-200 text-rose-500"}>
                  {t.enabled ? <><CheckCircle2 size={11} /> Enabled</> : "Disabled"}
                </Button>
                {!t.is_default && t.enabled && (
                  <Button size="sm" variant="outline" data-testid={`default-template-${t.id}`}
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: t.id, body: { is_default: true } })}
                    className="border-slate-200 text-slate-600">Set default</Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-500">
        Disabling a template hides it from the agent gallery — stores already using it keep their design. New stores and un-themed stores use the default.
      </p>
    </div>
  );
}
